import React from 'react';
import { CitationLink } from './CitationLink';

type CitationData = {
  platform: string;
  solution: string;
  tab: string;
};

type MessageBubbleMessage = {
  role: 'user' | 'assistant';
  content: string;
  citationMetadata?: Record<string, CitationData>;
};

type MessageBubbleProps = {
  message: MessageBubbleMessage;
  onCitationClick: (citationNumber: string, citationMetadata: Record<string, CitationData>) => void;
};

const parseLine = (
  line: string,
  key: number,
  citationMetadata: Record<string, CitationData> | undefined,
  onCitationClick: (num: string) => void
): React.ReactNode[] | string => {
  const elements: React.ReactNode[] = [];
  let currentIndex = 0;

  const pattern = /(\*\*.*?\*\*|\[\d+(?:[-,]\s*\d+)*\])/g;
  let match;

  while ((match = pattern.exec(line)) !== null) {
    if (match.index > currentIndex) {
      elements.push(line.substring(currentIndex, match.index));
    }

    const matchedText = match[0];

    if (matchedText.startsWith('**') && matchedText.endsWith('**')) {
      const boldContent = matchedText.slice(2, -2);
      elements.push(
        <strong
          key={`bold-${key}-${match.index}`}
          className="font-semibold text-gray-900 dark:text-gray-100"
        >
          {boldContent}
        </strong>
      );
    } else if (matchedText.startsWith('[') && matchedText.endsWith(']')) {
      const citationText = matchedText.slice(1, -1);
      const firstNumber = citationText.split(/[-,]/)[0].trim();
      const isClickable = !!(citationMetadata && citationMetadata[firstNumber]);
      elements.push(
        <sup key={`cite-${key}-${match.index}`}>
          <CitationLink
            citationText={citationText}
            firstNumber={firstNumber}
            isClickable={isClickable}
            onClick={onCitationClick}
          />
        </sup>
      );
    }

    currentIndex = match.index + matchedText.length;
  }

  if (currentIndex < line.length) {
    elements.push(line.substring(currentIndex));
  }

  return elements.length > 0 ? elements : line;
};

const renderMessageContent = (
  content: string,
  citationMetadata: Record<string, CitationData> | undefined,
  onCitationClick: (num: string) => void
): React.ReactNode => {
  const lines = content.split('\n');
  const elements: React.ReactNode[] = [];
  let currentListItems: React.ReactNode[] = [];
  let inList = false;

  lines.forEach((line, index) => {
    const trimmedLine = line.trim();
    const listMatch = trimmedLine.match(/^(\d+)\.\s+(.+)$/);

    if (listMatch) {
      inList = true;
      const listContent = parseLine(listMatch[2], index, citationMetadata, onCitationClick);
      currentListItems.push(
        <li key={`li-${index}`} className="mb-1">
          {listContent}
        </li>
      );
    } else {
      if (inList && currentListItems.length > 0) {
        elements.push(
          <ol key={`ol-${index}`} className="list-decimal list-inside mb-2 space-y-1">
            {currentListItems}
          </ol>
        );
        currentListItems = [];
        inList = false;
      }

      if (trimmedLine === '') {
        elements.push(<br key={`br-${index}`} />);
      } else {
        const lineContent = parseLine(line, index, citationMetadata, onCitationClick);
        elements.push(
          <p key={`p-${index}`} className="mb-2">
            {lineContent}
          </p>
        );
      }
    }
  });

  if (inList && currentListItems.length > 0) {
    elements.push(
      <ol key="ol-final" className="list-decimal list-inside mb-2 space-y-1">
        {currentListItems}
      </ol>
    );
  }

  return <div>{elements}</div>;
};

export function MessageBubble({ message, onCitationClick }: MessageBubbleProps) {
  const handleCitationClick = (num: string) => {
    onCitationClick(num, message.citationMetadata ?? {});
  };

  return (
    <div className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}>
      <div
        className={
          message.role === 'user'
            ? 'max-w-[85%] rounded-lg p-3 bg-blue-500 dark:bg-blue-600 text-white'
            : 'w-full text-gray-900 dark:text-gray-100'
        }
      >
        <div className="text-sm whitespace-pre-wrap">
          {message.role === 'assistant'
            ? renderMessageContent(message.content, message.citationMetadata, handleCitationClick)
            : message.content}
        </div>
      </div>
    </div>
  );
}
